.page {
  /* Fill the viewport with a constant gutter rather than centring inside a
     max-width — past that width the margins would grow instead of the table. */
  padding: var(--space-6) 0;
  width: 100%;
  min-height: 100vh;
  background: var(--bg-base);
}

/* Section */
.section {
  margin-bottom: var(--space-12);
}

.sectionHeader {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--space-5);
  flex-wrap: wrap;
  gap: var(--space-4);
}

.sectionTitle {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
}

.sectionIcon {
  font-size: 28px;
  line-height: 1;
}

.title {
  font-family: var(--font-sans);
  font-size: var(--text-2xl);
  font-weight: var(--weight-bold);
  color: var(--text-primary);
  margin: 0;
}

.subtitle {
  font-size: var(--text-sm);
  color: var(--text-muted);
  margin: var(--space-1) 0 0 0;
}

/* Filters */
.filters {
  display: flex;
  gap: var(--space-3);
  align-items: center;
}

.filters select {
  min-width: 120px;
}

/* Section Header with background image — full-width banner behind title + cards */
.sectionHeaderWrap {
  position: relative;
  margin-bottom: 40px;
  padding: 24px 28px 0 28px;
}

.sectionBgImage {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 300px;
  width: 100%;
  object-fit: cover;
  pointer-events: none;
  border-radius: 6px;
}

/* Gradient overlay to fade image edges into page background */
.sectionHeaderWrap::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 300px;
  border-radius: 6px;
  background:
    linear-gradient(to bottom, transparent 40%, var(--bg-base) 100%),
    linear-gradient(to right, var(--bg-base) 0%, transparent 10%, transparent 90%, var(--bg-base) 100%),
    linear-gradient(to bottom, var(--bg-base) 0%, transparent 15%);
  pointer-events: none;
}

.sectionHeaderContent {
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Selector buttons — Platform, Market, Time */
.selectors {
  display: flex;
  gap: 10px;
  align-items: center;
}

.selectorBtn {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 5px;
  cursor: pointer;
  box-shadow:
    0 8px 8px rgba(0, 0, 0, 0.1),
    0 4px 4px rgba(0, 0, 0, 0.1),
    0 2px 2px rgba(0, 0, 0, 0.1),
    0 1px 1px rgba(0, 0, 0, 0.1);
  transition: background var(--duration-fast);
}

.selectorBtn:hover {
  background: rgba(255, 255, 255, 0.15);
}

.selectorLabel {
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 700;
  color: #c3c3c3;
  text-transform: uppercase;
  white-space: nowrap;
  line-height: 17.5px;
}

.selectorChevron {
  width: 16px;
  height: 16px;
  color: #969696;
  display: flex;
  align-items: center;
  justify-content: center;
}

.selectorIcon {
  width: 20px;
  height: 20px;
  border-radius: 2px;
  object-fit: contain;
}

/* Loading / Error / Empty */
.loading,
.error,
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-12);
  gap: var(--space-4);
  color: var(--text-muted);
}

.error {
  color: var(--color-danger);
}

/* Grid - 2 columns. minmax(0, …) so long titles can't blow the track out. */
.grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-4);
}

@media (max-width: 1200px) {
  .grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* ========================================
   SECTION 2: Weekly Top Performers
   ======================================== */

.weeklyGrid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: var(--space-4);
}

@media (max-width: 1400px) {
  .weeklyGrid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .weeklyGrid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .weeklyGrid {
    grid-template-columns: 1fr;
  }
}

.platformColumn {
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.platformColumnHeader {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: var(--bg-elevated);
  border-top: 3px solid var(--border-subtle);
  font-weight: var(--weight-semi);
  font-size: var(--text-sm);
  color: var(--text-primary);
}

.platformColumnBody {
  padding: var(--space-2) 0;
}

.noData {
  padding: var(--space-6) var(--space-4);
  text-align: center;
  color: var(--text-muted);
  font-size: var(--text-sm);
}

.compactRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  text-decoration: none;
  transition: background var(--duration-fast);
}

.compactRow:hover {
  background: var(--bg-elevated);
}

.compactRank {
  flex-shrink: 0;
  width: 20px;
  font-size: var(--text-sm);
  font-weight: var(--weight-bold);
  color: var(--text-muted);
  text-align: center;
}

.compactPoster {
  flex-shrink: 0;
  width: 32px;
  height: 48px;
  object-fit: cover;
  border-radius: var(--radius-sm);
  background: var(--bg-elevated);
}

.compactTitle {
  flex: 1;
  font-size: var(--text-sm);
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.compactRevenue {
  flex-shrink: 0;
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--text-secondary);
}
